import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput", "previewContainer", "previewImage", "errorMessage", "textarea"]
  static values = {
    maxSizeMb: { type: Number, default: 2 },
    allowedFormats: { type: String, default: "jpeg,jpg,png,gif,webp" },
    compressionEnabled: { type: Boolean, default: true },
    compressionMaxPx: { type: Number, default: 1200 },
    compressionQuality: { type: Number, default: 0.8 }
  }

  selectFile() {
    this.fileInputTarget.click()
  }

  fileChanged() {
    const file = this.fileInputTarget.files[0]
    if (!file) {
      this.hidePreview()
      return
    }

    this.clearError()

    if (!this.validateFormat(file)) {
      this.showError(`Недопустимый формат. Разрешены: ${this.allowedFormatsValue}`)
      this.resetInput()
      return
    }

    if (!this.validateSize(file)) {
      this.showError(`Файл слишком большой (макс. ${this.maxSizeMbValue} МБ)`)
      this.resetInput()
      return
    }

    if (this.compressionEnabledValue && this.isCompressibleFormat(file)) {
      this.compressAndPreview(file)
    } else {
      this.showPreview(file)
    }

    this.toggleTextareaRequired()
  }

  removeFile() {
    this.resetInput()
    this.hidePreview()
    this.clearError()
    this.toggleTextareaRequired()
  }

  // --- Private ---

  toggleTextareaRequired() {
    if (!this.hasTextareaTarget) return

    const hasFile = this.fileInputTarget.files.length > 0
    this.textareaTarget.required = !hasFile
  }

  validateFormat(file) {
    const allowed = this.allowedFormatsValue.split(",").map(f => f.trim().toLowerCase())
    const extension = file.name.split(".").pop().toLowerCase()
    return allowed.includes(extension)
  }

  validateSize(file) {
    return file.size <= this.maxSizeMbValue * 1024 * 1024
  }

  isCompressibleFormat(file) {
    return /^image\/(jpeg|png|webp)$/i.test(file.type)
  }

  showPreview(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      this.previewImageTarget.src = e.target.result
      this.previewContainerTarget.classList.remove("is-hidden")
    }
    reader.readAsDataURL(file)
  }

  hidePreview() {
    this.previewContainerTarget.classList.add("is-hidden")
    this.previewImageTarget.src = ""
  }

  resetInput() {
    this.fileInputTarget.value = ""
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("is-hidden")
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ""
      this.errorMessageTarget.classList.add("is-hidden")
    }
  }

  async compressAndPreview(file) {
    try {
      const bitmap = await createImageBitmap(file)
      const { width, height } = bitmap
      const maxPx = this.compressionMaxPxValue

      let targetWidth = width
      let targetHeight = height

      if (width > maxPx || height > maxPx) {
        if (width > height) {
          targetWidth = maxPx
          targetHeight = Math.round((height / width) * maxPx)
        } else {
          targetHeight = maxPx
          targetWidth = Math.round((width / height) * maxPx)
        }
      }

      const canvas = document.createElement("canvas")
      canvas.width = targetWidth
      canvas.height = targetHeight

      const ctx = canvas.getContext("2d")
      ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight)
      bitmap.close()

      const outputType = file.type === "image/png" ? "image/png" : "image/jpeg"
      const quality = outputType === "image/png" ? undefined : this.compressionQualityValue

      const blob = await new Promise(resolve =>
        canvas.toBlob(resolve, outputType, quality)
      )

      const extension = outputType === "image/png" ? ".png" : ".jpg"
      const compressedName = file.name.replace(/\.[^.]+$/, extension)
      const compressedFile = new File([blob], compressedName, { type: outputType, lastModified: Date.now() })

      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(compressedFile)
      this.fileInputTarget.files = dataTransfer.files

      this.showPreview(compressedFile)
    } catch {
      this.showPreview(file)
    }
  }
}
